import {AfterViewInit, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, ViewChild, viewChild} from '@angular/core';
import {Subscription} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {FormatLocalKeyService} from '../../service/format-local-key.service';
import {Table, TableLazyLoadEvent} from 'primeng/table';
import {WimTreeNode} from '../../model/wim-tree.node';
import {GridHelperService} from '../../service/helper/grid-helper.service';
import {AppStateService} from '../../service/app-state.service';
import {GridRequest} from '../../model/grid-request.model';
import {CollectionDto} from '../../model/collection.model';
import {ConsoleLoggerService} from '../../service/console-logger.service';
import {NodeType} from '../../constants/node-type';
import {CantaaGridMeta} from '../../model/GridMeta';
import {GridMetaService} from '../../service/grid-meta.service';

@Component({
  template: ''
})
export abstract class BaseGridComponent<T> implements AfterViewInit, OnInit {

  protected appState = inject(AppStateService);
  protected gridHelperService = inject(GridHelperService);
  protected readonly translate = inject(TranslateService);
  protected readonly formatLocalKeyService = inject(FormatLocalKeyService);
  protected log = inject(ConsoleLoggerService);
  protected readonly cdRef = inject(ChangeDetectorRef);
  protected readonly destroyRef = inject(DestroyRef);
  protected gridMetaService = inject(GridMetaService);

  gridMeta!: CantaaGridMeta;
  public selectedItem: T | null = null;
  public lastSelectedNode: WimTreeNode | null = null;
  protected nodeTypes: string[] = []; // // a grid may represent multiple relation nodes (since project Responsible Employees and Mobile Locations)
  public totalRecords: number = 0;
  public gridItems: T[] = [];
  private pendingNode?: WimTreeNode;
  private pendingAction: string | null = null;

  private _dataTable?: Table;

  @ViewChild('dt')
  set dataTable(table: Table | undefined) {
    this._dataTable = table;
    if (table && this.pendingNode) {
      switch(this.pendingAction) {
        case 'onNodeChange':
          this.onNodeChange(this.pendingNode);
          break;
        case 'reFetchData':
          this.reFetchData();
          break;
      }
      this.pendingNode = undefined;
      this.pendingAction = null;
    }
  }

  get dataTable(): Table | undefined {
    return this._dataTable;
  }

  protected dataLoaded = false;
  protected dateFormat!: string;
  private langChangeSub?: Subscription;

  constructor() {
    this.initGridMeta();
  }


  ngOnInit(): void {

    console.log('BaseGridComponent.ngOnInit() called');
    this.updateDateFormat();
    this.langChangeSub = this.translate.onLangChange.subscribe(() =>
      this.updateDateFormat()
    );
  }

  ngAfterViewInit(): void {

    console.log('BaseGridComponent.ngAfterViewInit()', {
      component: this.constructor.name,
      dataTable: this.dataTable,
      gridIdentifier: this.getGridIdentifier(),
      gridMeta: this.gridMeta
    });

    const onSelectedNodeSubscription = this.appState.getSelectedNode$()
      .subscribe(async selectedNode => {
        this.log.debug('selected node', selectedNode);
        if (this.needReloadAfterNodeChanged(selectedNode)) {

          if (this.lastSelectedNode && this.lastSelectedNode.nodeType === selectedNode.nodeType) {
            // this.forceRefresh();
            this.initGridMeta();
            this.cdRef.markForCheck();
          }

          await this.onNodeChange(selectedNode);
        }
        this.lastSelectedNode = selectedNode;
      });

    this.destroyRef.onDestroy(() => {
      onSelectedNodeSubscription.unsubscribe();
    });
  }

  protected initGridMeta() {
    const gridIdentifier = this.getGridIdentifier();
    this.gridMeta = this.gridMetaService.getGridMeta(gridIdentifier, this.getDefaultSort(), this.getDefaultFilter());
    console.log(this.gridMeta);
  }

  protected getGridIdentifier() {
    const selectedNode = this.appState.selectedNodeValue;
    return this.constructor.name + selectedNode.key!;
  }

  protected getDefaultSort() {
    return [{field: 'id', order: 1}];
  }

  protected getDefaultFilter() {
    return this.gridMetaService.defaultFilterFallback;
  }

  public async onNodeChange(selectedNode: WimTreeNode) {

    if (!this.dataTable) {
      this.pendingNode = selectedNode;
      this.pendingAction = 'onNodeChange';
      console.warn('onNodeChange: Table not initialized yet', {
        component: this.constructor.name,
        dataTable: this.dataTable,
        gridIdentifier: this.getGridIdentifier(),
        gridMeta: this.gridMeta
      });
      return;
    }

    let lazyLoadMetadata = this.dataTable.createLazyLoadMetadata();
    await this.fetchData(selectedNode, lazyLoadMetadata);
  }

  public needReloadAfterNodeChanged(selectedNode: WimTreeNode): boolean {
    return !!selectedNode.key
      && (this.lastSelectedNode == null || this.lastSelectedNode.key !== selectedNode.key)
      && (this.nodeTypes.length === 0 || this.nodeTypes.includes(selectedNode.nodeType!));
  }

  public async reFetchData() {

    if (!this.dataTable) {
      this.pendingNode = this.appState.selectedNodeValue;
      this.pendingAction = 'reFetchData';
      console.warn('reFetchData: Table not initialized yet', {
        component: this.constructor.name,
        dataTable: this.dataTable,
        gridIdentifier: this.getGridIdentifier(),
        gridMeta: this.gridMeta
      });
      return;
    }

    const selectedNode = this.appState.selectedNodeValue;
    const lazyLoadMetadata = this.dataTable.createLazyLoadMetadata();
    await this.fetchData(selectedNode, lazyLoadMetadata);
  }

  public async fetchDataViaTableComponent(event: TableLazyLoadEvent) {
    let selectedNode = this.appState.selectedNodeValue
    await this.fetchData(selectedNode, event);
  }

  public async fetchData(selectedNode: WimTreeNode, lazyLoadMetadata: TableLazyLoadEvent) {

    let gridRequest = this.gridHelperService.createGridRequest(lazyLoadMetadata);

    const collection = await this.fetchGridItems(gridRequest, selectedNode);

    this.gridItems = collection.items;
    this.totalRecords = collection.totalNumberOfItems;
  }

  async fetchGridItems(gridRequest: GridRequest, selectedNode: WimTreeNode): Promise<CollectionDto<T>> {
    return {
      items: [],
      totalNumberOfItems: 0
    };
  }

  ngOnDestroy(): void {

    this.langChangeSub?.unsubscribe();
  }

  private updateDateFormat(): void {

    this.dateFormat = this.formatLocalKeyService.getDateFormat();
  }
}
